// THE CLASSIC EVENT PAGE — kept as a fallback beside the event workspace,
// reachable at /events/:eventId/classic (ClassicEventPage) from the
// workspace's sidebar. The workspace is the default; this is the page as it
// was before it (git: main@454c646:src/components/Event.jsx), changed only
// where the components it uses changed under it:
//   - import paths, for its folder;
//   - EventInfo, EventVideoAI, EventEmailTemplates and EventUsers no longer
//     wrap themselves in a FoldableSection, and Challenges no longer prints
//     its own title, so this page adds those back around them — it looks as
//     it always did.
// Its known faults are kept on purpose, since it is the old page: Cancel
// leaves the cancelled values on screen, and a snapshot arriving mid-edit
// replaces the draft. src/test/eventSettings.equivalence.test.jsx renders it
// beside the workspace and holds the two to the same fields and saves; delete
// it, its route and that test together when the fallback is no longer wanted.
import {
    Badge,
    Box,
    Button,
    Card,
    Flex,
    Heading,
    HStack,
    IconButton,
    Input,
    NativeSelect,
    Skeleton,
    Stack,
    Switch,
    Tag,
    Text,
    Separator,
    Textarea,
    Field,
    Spacer,
    FileUpload,
    Float,
    Dialog,
    Portal,
    CloseButton,
    Collapsible,
    useBreakpointValue,
} from '@chakra-ui/react'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
    updateEvent,
    uploadEventBackground,
    uploadEventLogo,
    uploadEventPrizeBackground,
    uploadEventSplash,
    createEvent,
    deleteEvent,
    generateValidatorCode,
    removeValidatorCode,
    fetchEventInfo,
    updateEventInfo,
    backfillEventStream,
    backfillGalleryAuthors,
} from '../../services/firebaseService'
import { DEFAULT_INFO_MARKDOWN } from '../../constants/defaultInfoMarkdown'
import { normalizeTier } from '../../constants/analyticsTiers'
import { Challenges } from '../../components/Challenges'
import { toaster } from '../../components/ui/toaster'
import EventQR from '../../components/EventQr'
import { useDocumentData } from '@kage1020/react-firebase-hooks/firestore';

import { EditableField } from '../../components/EditableField'
import { db } from '../../firebase'
import { doc } from 'firebase/firestore'
import { LuPencilLine, LuTrash, LuFileImage, LuChevronDown } from 'react-icons/lu'
import { ImageWithModal } from '../../components/ui/ImageWithModal'

import EventBackround from '../../components/EventBackround'
import EventInfo from '../../components/EventInfo'
import { EventUsers } from '../../components/EventUsers'
import ChangeEventTypeDialog from '../../components/ChangeEventTypeDialog'
import { CrowdsColorPicker } from '../../components/ui/CrowdsColorPicker'

import { FoldableSection } from '../../components/FoldableSection'
import { EventEmailTemplates } from '../../components/EventEmailTemplates'
import { EventVideoAI } from '../../components/EventVideoAI'
import { LinkWithQr } from '../../components/LinkWithQr'
import { useTranslation } from '../../i18n'
import { useEventTypes } from '../../contexts/EventTypesContext'
import { BASE_CREATE_DEFAULTS, DEFAULT_TYPE } from '../../constants/eventTypes'
import { displayWebappUrl, galleryUrl } from '../../utils/webappUrl'

function EditButtons({ creatingEvent, editing, setEditing, handleSave, handleCancel, saving }) {
    const { t } = useTranslation()
    return (
        <Flex justify="flex-end" gap={4}>
            {(!editing ?
                (<Button size="sm" colorPalette="brand" onClick={() => setEditing(true)}>
                    <LuPencilLine /> {t('common.edit')}
                </Button>) :
                (<>
                    <Button size="sm" colorPalette="brand" loading={saving} onClick={handleSave} loadingText={t('common.saving')}>
                        {creatingEvent ? t('events.createEvent') : t('common.save')}
                    </Button>
                    <Button disabled={saving} size="sm" variant="outline" onClick={handleCancel}>
                        {t('common.cancel')}
                    </Button>
                </>))}
        </Flex >
    )
}

function CreateEventData(eventTypeId, typeConfig) {
    const [eventData] = useState({
        ...BASE_CREATE_DEFAULTS,
        ...(typeConfig?.defaults || {}),
        eventType: eventTypeId,
    })

    return [eventData, false, null];
}

export default function Event({ eventId, creatingEvent = false, onCreated = null, defaultOpen = false, eventType = DEFAULT_TYPE }) {
    const navigate = useNavigate()
    const { t, lang } = useTranslation()
    const { isSysAdmin, isAdmin, eventRoles } = useAuth()
    const { getTypeConfig, typesList } = useEventTypes()
    const isMobile = useBreakpointValue({ base: true, md: false })
    // On mobile, every collapsible section starts closed regardless of the parent's preference.
    const sectionDefaultOpen = isMobile ? false : defaultOpen
    // When creating, seed from the chosen type's config; when editing, read the
    // event doc. Both refs are called unconditionally (rules-of-hooks); the create
    // path passes a null ref so useDocumentData never builds a doc() with no id.
    const createTypeConfig = getTypeConfig(eventType)
    const createdEventData = CreateEventData(eventType, createTypeConfig)
    const fetchedEventData = useDocumentData(creatingEvent ? null : doc(db, 'events', eventId));
    const [eventData, loading] = creatingEvent ? createdEventData : fetchedEventData;
    const eventTypeId = creatingEvent ? eventType : (eventData?.eventType || DEFAULT_TYPE);
    const typeConfig = getTypeConfig(eventTypeId);
    // The URL shown in the event's QR: local webapp in dev, stored/composed
    // production URL otherwise. Both use the /{slug}/{eventId} scheme.
    const eventWebappUrl = eventId
        ? displayWebappUrl({ storedUrl: eventData?.webappUrl, typeConfig, eventType: eventTypeId, eventId })
        : undefined;
    const isHidden = (sectionId) => (typeConfig?.hiddenSections || []).includes(sectionId);
    const canPrizes = typeConfig?.capabilities?.prizes !== false;
    const typeAdminRole = typeConfig?.adminRole || 'eventadmin';

    const [editedEvent, setEditedEvent] = useState(null)
    const [editing, setEditing] = useState(creatingEvent)
    const [backgroundFile, setBackgroundFile] = useState(null)
    const [logoFile, setLogoFile] = useState(null)
    const [prizeBgFile, setPrizeBgFile] = useState(null)
    const [splashFile, setSplashFile] = useState(null)
    const [eventRole, setEventRole] = useState(null)
    const [saving, setSaving] = useState(false)
    const [refreshingAuthors, setRefreshingAuthors] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [pendingType, setPendingType] = useState(null)

    const [infoMarkdownOriginal, setInfoMarkdownOriginal] = useState(null)
    const [editedInfoMarkdown, setEditedInfoMarkdown] = useState(null)
    const [infoLoading, setInfoLoading] = useState(!creatingEvent)

    const hasUnsavedChanges = editing && (
        (editedEvent && eventData && JSON.stringify(editedEvent) !== JSON.stringify(eventData))
        || (infoMarkdownOriginal !== null && editedInfoMarkdown !== infoMarkdownOriginal)
    )

    useEffect(() => {
        if (!hasUnsavedChanges) return
        const handler = (e) => { e.preventDefault() }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [hasUnsavedChanges])

    useEffect(() => {
        if (eventData) {
            setEditedEvent(eventData)
        }
    }, [eventData])

    useEffect(() => {
        const role = eventRoles.find(role => role.eventid === eventId)
        setEventRole(role ? role.role : null)
    }, [eventRoles, eventId])

    useEffect(() => {
        if (!eventId || creatingEvent) return
        let cancelled = false
        setInfoLoading(true)
        fetchEventInfo(eventId)
            .then((data) => {
                if (cancelled) return
                const md = data?.markdown ?? DEFAULT_INFO_MARKDOWN
                setInfoMarkdownOriginal(md)
                setEditedInfoMarkdown(md)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to load event info', err)
                setInfoMarkdownOriginal(DEFAULT_INFO_MARKDOWN)
                setEditedInfoMarkdown(DEFAULT_INFO_MARKDOWN)
            })
            .finally(() => { if (!cancelled) setInfoLoading(false) })
        return () => { cancelled = true }
    }, [eventId, creatingEvent])

    const canEdit = isSysAdmin || eventRole === 'eventadmin' || eventRole === typeAdminRole

    const handleEventField = (key, value) => {
        setEditedEvent((prev) => ({ ...prev, [key]: value }))
    }

    const handleDeleteEvent = async (eventId) => {
        try {
            await deleteEvent(eventId)
            toaster.create({ title: t('events.eventDeleted'), status: 'success' })
            // replace: Back must not land on the page of an event that no longer exists
            navigate('/events', { replace: true })
        } catch (err) {
            toaster.create({ title: t('events.eventDeleteFailed'), description: err.message, status: 'error' })
        }
    }

    const handleSave = useCallback(async () => {
        try {
            setSaving(true)
            let backgroundUrl = editedEvent.backgroundUrl
            if (backgroundFile) {
                backgroundUrl = await uploadEventBackground(eventId, backgroundFile)
            }
            let logoUrl = editedEvent.logoUrl
            if (logoFile) {
                logoUrl = await uploadEventLogo(eventId, logoFile)
            }
            let prizeBackgroundUrl = editedEvent.prizeBackgroundUrl
            if (prizeBgFile) {
                prizeBackgroundUrl = await uploadEventPrizeBackground(eventId, prizeBgFile)
            }
            let splashUrl = editedEvent.splashUrl
            if (splashFile) {
                splashUrl = await uploadEventSplash(eventId, splashFile)
            }
            const updates = creatingEvent ? editedEvent : {}
            if (creatingEvent) {
                updates.backgroundUrl = backgroundUrl;
                updates.logoUrl = logoUrl;
                updates.prizeBackgroundUrl = prizeBackgroundUrl;
                updates.splashUrl = splashUrl;
            } else {
                if (editedEvent.name && eventData.name !== editedEvent.name) updates.name = editedEvent.name
                if (eventData.backgroundUrl !== backgroundUrl) updates.backgroundUrl = backgroundUrl
                if (editedEvent.backgroundColor && eventData.backgroundColor !== editedEvent.backgroundColor) updates.backgroundColor = editedEvent.backgroundColor
                if (editedEvent.colorBg && eventData.colorBg !== editedEvent.colorBg) updates.colorBg = editedEvent.colorBg
                if (editedEvent.colorSurface && eventData.colorSurface !== editedEvent.colorSurface) updates.colorSurface = editedEvent.colorSurface
                if (editedEvent.colorPrimary && eventData.colorPrimary !== editedEvent.colorPrimary) updates.colorPrimary = editedEvent.colorPrimary
                if (editedEvent.colorText && eventData.colorText !== editedEvent.colorText) updates.colorText = editedEvent.colorText
                if (editedEvent.prizeBackgroundColor && eventData.prizeBackgroundColor !== editedEvent.prizeBackgroundColor) updates.prizeBackgroundColor = editedEvent.prizeBackgroundColor
                if (eventData.logoUrl !== logoUrl) updates.logoUrl = logoUrl
                if (eventData.prizeBackgroundUrl !== prizeBackgroundUrl) updates.prizeBackgroundUrl = prizeBackgroundUrl
                if (eventData.splashUrl !== splashUrl) updates.splashUrl = splashUrl
                if (editedEvent.challenges && eventData.challenges !== editedEvent.challenges) updates.challenges = editedEvent.challenges
                if (editedEvent.defaultModerated !== undefined && eventData.defaultModerated !== editedEvent.defaultModerated) updates.defaultModerated = editedEvent.defaultModerated
                if (editedEvent.challengeMode !== undefined && eventData.challengeMode !== editedEvent.challengeMode) updates.challengeMode = editedEvent.challengeMode
                if (editedEvent.requireAuth !== undefined && eventData.requireAuth !== editedEvent.requireAuth) updates.requireAuth = editedEvent.requireAuth
                if (editedEvent.maxRejections !== undefined && eventData.maxRejections !== editedEvent.maxRejections) updates.maxRejections = editedEvent.maxRejections
                if (editedEvent.prizeConfig !== undefined) updates.prizeConfig = editedEvent.prizeConfig
                if (editedEvent.challengePrompt !== undefined) updates.challengePrompt = editedEvent.challengePrompt
                if (editedEvent.prizePrompt !== undefined && eventData.prizePrompt !== editedEvent.prizePrompt) updates.prizePrompt = editedEvent.prizePrompt
                if (editedEvent.maxVideosPerUser !== undefined && eventData.maxVideosPerUser !== editedEvent.maxVideosPerUser) updates.maxVideosPerUser = editedEvent.maxVideosPerUser
                if (editedEvent.receptionEnabled !== undefined) updates.receptionEnabled = editedEvent.receptionEnabled
                if (editedEvent.receptionStart !== undefined) updates.receptionStart = editedEvent.receptionStart
                if (editedEvent.receptionEnd !== undefined) updates.receptionEnd = editedEvent.receptionEnd
                if (editedEvent.maxSegmentDuration !== undefined && eventData.maxSegmentDuration !== editedEvent.maxSegmentDuration) updates.maxSegmentDuration = editedEvent.maxSegmentDuration
                if (editedEvent.maxRecordingDuration !== undefined && eventData.maxRecordingDuration !== editedEvent.maxRecordingDuration) updates.maxRecordingDuration = editedEvent.maxRecordingDuration
                if (editedEvent.streamEnabled !== undefined && eventData.streamEnabled !== editedEvent.streamEnabled) updates.streamEnabled = editedEvent.streamEnabled
                if (editedEvent.galleryPublic !== undefined && eventData.galleryPublic !== editedEvent.galleryPublic) updates.galleryPublic = editedEvent.galleryPublic
                if (editedEvent.tier !== undefined && eventData.tier !== editedEvent.tier) updates.tier = editedEvent.tier
            }
            if (creatingEvent) {
                await createEvent(updates, lang)
            } else {
                await updateEvent(eventId, updates)
                if (
                    infoMarkdownOriginal !== null &&
                    editedInfoMarkdown !== null &&
                    editedInfoMarkdown !== infoMarkdownOriginal
                ) {
                    await updateEventInfo(eventId, editedInfoMarkdown)
                    setInfoMarkdownOriginal(editedInfoMarkdown)
                }
            }

            toaster.create({ title: t('events.eventUpdated'), status: 'success' })
        } catch (err) {
            toaster.create({ title: t('events.eventUpdateFailed'), description: err.message, status: 'error' })
        }
        setEditing(false)
        setSaving(false)
        if (creatingEvent && onCreated) onCreated()
    }, [editedEvent, eventData, eventId, creatingEvent, onCreated, backgroundFile, logoFile, prizeBgFile, splashFile, editedInfoMarkdown, infoMarkdownOriginal, t, lang])

    const handleBackgroundChange = useCallback((background, element) => {
        setEditedEvent((prev) => ({ ...prev, [element]: background }))
    }, []);

    const handleCancel = useCallback(() => {
        setEditing(false)
        if (infoMarkdownOriginal !== null) setEditedInfoMarkdown(infoMarkdownOriginal)
        if (creatingEvent && onCreated) onCreated()
    }, [creatingEvent, onCreated, infoMarkdownOriginal])

    // Access guard lives after all hooks so hook order stays constant (rules-of-hooks).
    if (!isAdmin) return

    if (loading || !eventData) return (
        <Skeleton height="120px" borderRadius="md" />
    )

    return (

        <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
            <Card.Header>
                <Flex align="center" mb={2} wrap="wrap" gap={2}>
                    {!editing && eventId ? (
                        <Heading cursor="pointer" _hover={{ textDecoration: 'underline' }} onClick={() => navigate(`/events/${eventId}`)}>
                            {eventData?.name || t('events.untitled')}
                        </Heading>
                    ) : (
                        <EditableField editing={editing} fieldname={t('events.eventName')} data={!editing ? eventData : editedEvent} dataelement={"name"} handler={handleEventField} isHeading={!editing} placeholder={{ edit: t('events.eventNameEditPlaceholder'), preview: t('events.eventNamePlaceholder') }} />
                    )}
                    <Spacer />
                    {eventId && <Tag.Root cursor="pointer" fontFamily="'JetBrains Mono', monospace" fontSize="xs" onClick={() => navigate(`/events/${eventId}`)}>{eventId}</Tag.Root>}
                    {eventId && eventRoles.find(role => role.eventid === eventId)?.role && <Tag.Root>{eventRoles.find(role => role.eventid === eventId)?.role}</Tag.Root>}
                    {typeConfig && <Tag.Root colorPalette={typeConfig.tagColor}>{typeConfig.label?.[lang] || typeConfig.label?.en || eventTypeId}</Tag.Root>}
                    {isSysAdmin && eventId && !creatingEvent && !editing && (
                        <NativeSelect.Root size="xs" width="150px">
                            <NativeSelect.Field
                                aria-label={t('eventTypes.changeTitle')}
                                value={eventTypeId}
                                onChange={(e) => { if (e.target.value !== eventTypeId) setPendingType(e.target.value) }}
                                bg="var(--mc-input-bg)"
                            >
                                {typesList.map((tc) => (
                                    <option key={tc.id} value={tc.id}>{tc.label?.[lang] || tc.label?.en || tc.id}</option>
                                ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                        </NativeSelect.Root>
                    )}
                </Flex>
                <Flex align="center" wrap="wrap" gap={2}>
                    {eventId && <HStack gap={3} wrap="wrap">
                        {/* Moderation is open to anyone with access to the event,
                            whatever its type: a wedding auto-accepts by default
                            but its videos can still be moderated by hand. */}
                        {!editing && <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/events/${eventId}/moderation`)}
                        >
                            {t('events.moderation')}
                        </Button>}
                        {!editing && (isSysAdmin || eventRole === 'eventadmin' || eventRole === typeAdminRole) &&
                            (<Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/events/${eventId}/videos`)}
                            >
                                {t('events.videos')}
                            </Button>)
                        }
                        {!editing && (isSysAdmin || eventRole === 'eventadmin' || eventRole === typeAdminRole) &&
                            (<Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/events/${eventId}/reels`)}
                            >
                                {t('events.reels')}
                            </Button>)
                        }
                        {!editing && canPrizes && eventData?.challengeMode && (isSysAdmin || eventRole === 'eventadmin') &&
                            (<Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/events/${eventId}/prizes`)}
                            >
                                {t('events.prizes')}
                            </Button>)
                        }
                        {!editing && (isSysAdmin || eventRole === 'eventadmin') &&
                            (<Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/events/${eventId}/analytics`)}
                            >
                                {t('analytics.navLink')}
                            </Button>)
                        }
                    </HStack>}
                    <Spacer />
                    {canEdit &&
                        <EditButtons
                            creatingEvent={creatingEvent}
                            editing={editing}
                            setEditing={setEditing}
                            handleSave={handleSave}
                            handleCancel={handleCancel}
                            saving={saving}
                        />
                    }
                    {isSysAdmin && eventId && <IconButton
                        aria-label={t('events.deleteEventAria')}
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        <LuTrash />
                    </IconButton>}
                </Flex>
            </Card.Header>
            <Separator />
            <Card.Body>
                <Stack gap={4}>
                    <Flex direction={{ base: 'column', md: 'row' }} gap={4} justify="space-between">
                        <Stack gap={4} width="100%" >
                            <FoldableSection title={t('events.images')} defaultOpen={sectionDefaultOpen}>
                                <Flex gap={6} wrap="wrap">
                                    {!isHidden('challengeBackground') && <EventBackround name={t('events.challengeBackground')} eventData={!editing ? eventData : editedEvent} imagePropName={"backgroundUrl"} colorPropName={"backgroundColor"} editing={editing} setImageFile={setBackgroundFile} handleBackgroundChange={handleBackgroundChange} />}
                                    {!isHidden('logo') && <EventBackround name={t('events.logo')} eventData={!editing ? eventData : editedEvent} imagePropName={"logoUrl"} colorPropName={""} editing={editing} setImageFile={setLogoFile} handleBackgroundChange={handleBackgroundChange} />}
                                    {!isHidden('prizeBackground') && <EventBackround name={t('events.prizeBackground')} eventData={!editing ? eventData : editedEvent} imagePropName={"prizeBackgroundUrl"} colorPropName={"prizeBackgroundColor"} editing={editing} setImageFile={setPrizeBgFile} handleBackgroundChange={handleBackgroundChange} />}
                                    {!isHidden('splash') && <EventBackround name={t('events.splashScreen')} eventData={!editing ? eventData : editedEvent} imagePropName={"splashUrl"} colorPropName={""} editing={editing} setImageFile={setSplashFile} handleBackgroundChange={handleBackgroundChange} />}
                                </Flex>
                            </FoldableSection>

                            {!isHidden('themeColors') && (<FoldableSection title={t('events.themeColors')} defaultOpen={sectionDefaultOpen}>
                                <Flex gap={4} wrap="wrap">
                                    <CrowdsColorPicker
                                        label={t('events.backgroundColor')}
                                        color={editedEvent?.colorBg ?? '#0f172a'}
                                        editing={editing}
                                        setColor={(color) => handleEventField('colorBg', color)}
                                        maxW={{ base: '100%', sm: '200px' }}
                                    />
                                    <CrowdsColorPicker
                                        label={t('events.surfaceColor')}
                                        color={editedEvent?.colorSurface ?? '#1e293b'}
                                        editing={editing}
                                        setColor={(color) => handleEventField('colorSurface', color)}
                                        maxW={{ base: '100%', sm: '200px' }}
                                    />
                                    <CrowdsColorPicker
                                        label={t('events.primaryColor')}
                                        color={editedEvent?.colorPrimary ?? '#8b5cf6'}
                                        editing={editing}
                                        setColor={(color) => handleEventField('colorPrimary', color)}
                                        maxW={{ base: '100%', sm: '200px' }}
                                    />
                                    <CrowdsColorPicker
                                        label={t('events.textColor')}
                                        color={editedEvent?.colorText ?? '#f1f5f9'}
                                        editing={editing}
                                        setColor={(color) => handleEventField('colorText', color)}
                                        maxW={{ base: '100%', sm: '200px' }}
                                    />
                                </Flex>
                            </FoldableSection>)}

                            <FoldableSection title={t('challenges.title')} defaultOpen={sectionDefaultOpen}>
                                <Separator />
                                <Heading size="md">{t('challenges.title')}</Heading>
                                <Challenges form={editedEvent} setForm={setEditedEvent} editing={editing} />


                            <Flex gap={4} align="flex-end" wrap="wrap">
                                <Field.Root flex="1">
                                    <Field.Label>{t('events.challengePrompt')}</Field.Label>
                                    <Input
                                        value={editedEvent?.challengePrompt?.text ?? ''}
                                        onChange={(e) => handleEventField('challengePrompt', { ...editedEvent?.challengePrompt, text: e.target.value })}
                                        disabled={!editing}
                                        placeholder={t('events.challengePromptPlaceholder')}
                                    />
                                </Field.Root>
                                <CrowdsColorPicker
                                    label={t('events.promptColor')}
                                    color={editedEvent?.challengePrompt?.color ?? '#ffffff'}
                                    editing={editing}
                                    setColor={(color) => handleEventField('challengePrompt', { ...editedEvent?.challengePrompt, color })}
                                    maxW={{ base: '100%', sm: '200px' }}
                                />
                            </Flex>
                            </FoldableSection>

                            {!isHidden('maxVideos') && !editedEvent?.challengeMode && (
                                <Field.Root>
                                    <Field.Label>{t('events.maxVideosPerUser')}</Field.Label>
                                    <Flex align="center" gap={3}>
                                        <Input
                                            type="number"
                                            min={0}
                                            value={editedEvent?.maxVideosPerUser ?? ''}
                                            onChange={(e) => handleEventField('maxVideosPerUser', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                                            disabled={!editing}
                                            w={{ base: '100%', sm: '100px' }}
                                            placeholder={t('events.maxVideosPlaceholder')}
                                        />
                                        <Text fontSize="sm" color="fg.muted">
                                            {t('events.maxVideosHelp')}
                                        </Text>
                                    </Flex>
                                </Field.Root>
                            )}


                            {!isHidden('prizeMode') && (<FoldableSection title={t('events.prizeMode')} defaultOpen={sectionDefaultOpen}>
                            <Flex align="center" gap={3}>
                                <Switch.Root
                                    checked={editedEvent?.challengeMode ?? false}
                                    onCheckedChange={(details) => handleEventField('challengeMode', details.checked)}
                                    disabled={!editing}
                                >
                                    <Switch.HiddenInput />
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                    <Switch.Label>{t('events.enablePrizeMode')}</Switch.Label>
                                </Switch.Root>
                                <Text fontSize="sm" color="fg.muted">
                                    {t('events.enablePrizeModeHelp')}
                                </Text>
                            </Flex>

                            {editedEvent?.challengeMode && (
                                <Stack gap={4} pl={4}>
                                    <Flex align="center" gap={3}>
                                        <Switch.Root
                                            checked={editedEvent?.requireAuth ?? false}
                                            onCheckedChange={(details) => handleEventField('requireAuth', details.checked)}
                                            disabled={!editing}
                                        >
                                            <Switch.HiddenInput />
                                            <Switch.Control>
                                                <Switch.Thumb />
                                            </Switch.Control>
                                            <Switch.Label>{t('events.requireLogin')}</Switch.Label>
                                        </Switch.Root>
                                        <Text fontSize="sm" color="fg.muted">
                                            {t('events.requireLoginHelp')}
                                        </Text>
                                    </Flex>

                                    <Field.Root>
                                        <Field.Label>{t('events.maxRejections')}</Field.Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={10}
                                            value={editedEvent?.maxRejections ?? 3}
                                            onChange={(e) => handleEventField('maxRejections', parseInt(e.target.value) || 3)}
                                            disabled={!editing}
                                            w={{ base: '100%', sm: '100px' }}
                                        />
                                    </Field.Root>

                                    <Heading size="sm">{t('events.prizeConfig')}</Heading>
                                    <Field.Root>
                                        <Field.Label>{t('events.prizeTitle')}</Field.Label>
                                        <Input
                                            value={editedEvent?.prizeConfig?.title ?? ''}
                                            onChange={(e) => handleEventField('prizeConfig', { ...editedEvent?.prizeConfig, title: e.target.value })}
                                            disabled={!editing}
                                            placeholder={t('events.prizeTitlePlaceholder')}
                                        />
                                    </Field.Root>
                                    <Field.Root>
                                        <Field.Label>{t('events.prizeDescription')}</Field.Label>
                                        <Textarea
                                            value={editedEvent?.prizeConfig?.description ?? ''}
                                            onChange={(e) => handleEventField('prizeConfig', { ...editedEvent?.prizeConfig, description: e.target.value })}
                                            disabled={!editing}
                                            placeholder={t('events.prizeDescriptionPlaceholder')}
                                        />
                                    </Field.Root>
                                    <Field.Root>
                                        <Field.Label>{t('events.prizePrompt')}</Field.Label>
                                        <Textarea
                                            value={editedEvent?.prizePrompt ?? ''}
                                            onChange={(e) => handleEventField('prizePrompt', e.target.value)}
                                            disabled={!editing}
                                            placeholder={t('events.prizePromptPlaceholder')}
                                        />
                                    </Field.Root>

                                    {eventId && (
                                        <>
                                            <Heading size="sm">{t('events.validatorCodes')}</Heading>
                                            <Text fontSize="sm" color="fg.muted">
                                                {t('events.validatorCodesHelp')}
                                            </Text>
                                            <Stack gap={2}>
                                                {(editedEvent?.validatorCodes || []).map((vc, idx) => {
                                                    let validatorBase = import.meta.env.VITE_PUBLIC_VALIDATOR_URL || window.location.origin
                                                    if (import.meta.env.DEV) {
                                                        const { protocol, hostname } = window.location
                                                        validatorBase = `${protocol}//${hostname}:5176`
                                                    }
                                                    const validatorUrl = `${validatorBase}/validate?event=${eventId}&code=${vc.code}`
                                                    return (
                                                        <LinkWithQr
                                                            key={idx}
                                                            url={validatorUrl}
                                                            qrTitle={t('events.validatorQrTitle')}
                                                            downloadName={`validator-qr-${eventId}`}
                                                            copyLabel={t('events.copyValidatorUrl')}
                                                            leading={<Badge fontSize="md" px={2} fontFamily="mono">{vc.code}</Badge>}
                                                        >
                                                            {editing && (
                                                                <IconButton
                                                                    aria-label={t('events.removeCode')}
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    onClick={async () => {
                                                                        await removeValidatorCode(eventId, vc)
                                                                        setEditedEvent(prev => ({
                                                                            ...prev,
                                                                            validatorCodes: (prev.validatorCodes || []).filter((_, i) => i !== idx)
                                                                        }))
                                                                    }}
                                                                >
                                                                    <LuTrash />
                                                                </IconButton>
                                                            )}
                                                        </LinkWithQr>
                                                    )
                                                })}
                                            </Stack>
                                            {editing && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={async () => {
                                                        await generateValidatorCode(eventId)
                                                    }}
                                                >
                                                    {t('events.generateValidatorCode')}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            )}
                            </FoldableSection>)}


                            <FoldableSection title={t('events.recordingSettings')} defaultOpen={sectionDefaultOpen}>
                                <Flex gap={4} wrap="wrap">
                                    <Field.Root flex="1">
                                        <Field.Label>{t('events.maxSegmentDuration')}</Field.Label>
                                        <Text fontSize="xs" color="fg.muted">{t('events.maxSegmentDurationHelp')}</Text>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={300}
                                            value={editedEvent?.maxSegmentDuration ?? 5}
                                            onChange={(e) => handleEventField('maxSegmentDuration', parseFloat(e.target.value) || 5)}
                                            disabled={!editing}
                                            w={{ base: '100%', sm: '100px' }}
                                        />
                                    </Field.Root>
                                    <Field.Root flex="1">
                                        <Field.Label>{t('events.maxRecordingDuration')}</Field.Label>
                                        <Text fontSize="xs" color="fg.muted">{t('events.maxRecordingDurationHelp')}</Text>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={600}
                                            value={editedEvent?.maxRecordingDuration ?? 15}
                                            onChange={(e) => handleEventField('maxRecordingDuration', parseFloat(e.target.value) || 15)}
                                            disabled={!editing}
                                            w={{ base: '100%', sm: '100px' }}
                                        />
                                    </Field.Root>
                                </Flex>
                            </FoldableSection>

                            <FoldableSection title={t('events.videoReception')} defaultOpen={sectionDefaultOpen}>
                            <Flex align="center" gap={3}>
                                <Switch.Root
                                    checked={editedEvent?.receptionEnabled ?? true}
                                    onCheckedChange={(details) => handleEventField('receptionEnabled', details.checked)}
                                    disabled={!editing}
                                >
                                    <Switch.HiddenInput />
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                    <Switch.Label>{t('events.acceptSubmissions')}</Switch.Label>
                                </Switch.Root>
                                <Text fontSize="sm" color="fg.muted">
                                    {t('events.acceptSubmissionsHelp')}
                                </Text>
                            </Flex>
                            {editedEvent?.receptionEnabled !== false && (
                                <Flex gap={4} wrap="wrap">
                                    <Field.Root flex="1">
                                        <Field.Label>{t('events.receptionStart')}</Field.Label>
                                        <Input
                                            type="datetime-local"
                                            value={editedEvent?.receptionStart || ''}
                                            onChange={(e) => handleEventField('receptionStart', e.target.value)}
                                            disabled={!editing}
                                        />
                                    </Field.Root>
                                    <Field.Root flex="1">
                                        <Field.Label>{t('events.receptionEnd')}</Field.Label>
                                        <Input
                                            type="datetime-local"
                                            value={editedEvent?.receptionEnd || ''}
                                            onChange={(e) => handleEventField('receptionEnd', e.target.value)}
                                            disabled={!editing}
                                        />
                                    </Field.Root>
                                </Flex>
                            )}
                            </FoldableSection>

                            <FoldableSection title={t('events.streamingAndGallery')} defaultOpen={sectionDefaultOpen}>
                                <Stack gap={3}>
                                    <Switch.Root
                                        checked={!!editedEvent?.streamEnabled}
                                        disabled={!isSysAdmin || !editing}
                                        onCheckedChange={(details) => handleEventField('streamEnabled', details.checked)}
                                    >
                                        <Switch.HiddenInput />
                                        <Switch.Control>
                                            <Switch.Thumb />
                                        </Switch.Control>
                                        <Switch.Label>{t('events.enableStreaming')}</Switch.Label>
                                    </Switch.Root>

                                    {!isSysAdmin && (
                                        <Text fontSize="xs" color="fg.muted">{t('events.streamingSysadminOnly')}</Text>
                                    )}

                                    {editedEvent?.streamEnabled && (
                                        <>
                                            <Switch.Root
                                                checked={!!editedEvent?.galleryPublic}
                                                disabled={!isSysAdmin || !editing}
                                                onCheckedChange={(details) => handleEventField('galleryPublic', details.checked)}
                                            >
                                                <Switch.HiddenInput />
                                                <Switch.Control>
                                                    <Switch.Thumb />
                                                </Switch.Control>
                                                <Switch.Label>{t('events.galleryPublic')}</Switch.Label>
                                            </Switch.Root>
                                            <Text fontSize="xs" color="fg.muted">{t('events.galleryPublicHint')}</Text>
                                        </>
                                    )}

                                    {eventId && !creatingEvent && editedEvent?.streamEnabled && (
                                        <Box>
                                            <Text fontSize="sm" mb={1}>{t('events.galleryUrl')}</Text>
                                            <LinkWithQr
                                                url={galleryUrl(eventId)}
                                                qrTitle={t('events.galleryQrTitle')}
                                                downloadName={`gallery-qr-${eventId}`}
                                            />
                                        </Box>
                                    )}

                                    {eventData?.streamBackfillPending && (
                                        <HStack>
                                            <Text fontSize="xs" color="orange.400">{t('events.backfillPending')}</Text>
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                onClick={async () => {
                                                    try {
                                                        const res = await backfillEventStream(eventId)
                                                        toaster.create({ title: t('events.sendRemainingDone', { count: res.sent, remaining: res.remaining }), status: 'success' })
                                                    } catch (err) {
                                                        toaster.create({ title: err.message, status: 'error' })
                                                    }
                                                }}
                                            >
                                                {t('events.sendRemaining')}
                                            </Button>
                                        </HStack>
                                    )}

                                    {eventId && !creatingEvent && editedEvent?.streamEnabled && (
                                        <Box>
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                loading={refreshingAuthors}
                                                onClick={async () => {
                                                    setRefreshingAuthors(true)
                                                    try {
                                                        const res = await backfillGalleryAuthors(eventId)
                                                        toaster.create({ title: t('events.backfillAuthorsDone', { updated: res.updated, scanned: res.scanned, recovered: res.recovered }), status: 'success' })
                                                    } catch (err) {
                                                        toaster.create({ title: err.message, status: 'error' })
                                                    } finally {
                                                        setRefreshingAuthors(false)
                                                    }
                                                }}
                                            >
                                                {t('events.backfillAuthors')}
                                            </Button>
                                            <Text fontSize="xs" color="fg.muted" mt={1}>{t('events.backfillAuthorsHint')}</Text>
                                        </Box>
                                    )}
                                </Stack>
                            </FoldableSection>


                            {!isHidden('analytics') && isSysAdmin && (
                                <FoldableSection title={t('analytics.title')} defaultOpen={sectionDefaultOpen}>
                                    <Field.Root>
                                        <Field.Label>{t('analytics.tier')}</Field.Label>
                                        <NativeSelect.Root w={{ base: '100%', sm: '200px' }} disabled={!editing}>
                                            <NativeSelect.Field
                                                value={normalizeTier(editedEvent?.tier)}
                                                onChange={(e) => handleEventField('tier', e.target.value)}
                                            >
                                                <option value="free">{t('analytics.tierFree')}</option>
                                                <option value="pro">{t('analytics.tierPro')}</option>
                                            </NativeSelect.Field>
                                            <NativeSelect.Indicator />
                                        </NativeSelect.Root>
                                    </Field.Root>
                                </FoldableSection>
                            )}

                            {!isHidden('moderationSettings') && (<>
                            <Separator />
                            <FoldableSection title={t('events.moderationSettings')} defaultOpen={sectionDefaultOpen}>
                            <Flex align="center" gap={3}>
                                <Switch.Root
                                    checked={editedEvent?.defaultModerated ?? false}
                                    onCheckedChange={(details) => handleEventField('defaultModerated', details.checked)}
                                    disabled={!editing}
                                >
                                    <Switch.HiddenInput />
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                    <Switch.Label>{t('events.autoApprove')}</Switch.Label>
                                </Switch.Root>
                                <Text fontSize="sm" color="fg.muted">
                                    {t('events.autoApproveHelp')}
                                </Text>
                            </Flex>
                            </FoldableSection>
                            </>)}

                            {eventId && !creatingEvent && (
                                <FoldableSection title={t('events.infoSection')} defaultOpen={false}>
                                    <EventInfo
                                        loading={infoLoading}
                                        editing={editing}
                                        canEdit={canEdit}
                                        markdown={editedInfoMarkdown}
                                        onChange={setEditedInfoMarkdown}
                                    />
                                </FoldableSection>
                            )}

                            {isMobile && (
                                <FoldableSection title={t('qr.title')} defaultOpen={false}>
                                    <EventQR eventId={eventId} webappUrl={eventWebappUrl} hideTitle />
                                </FoldableSection>
                            )}
                        </Stack>

                        {!isMobile && <EventQR eventId={eventId} webappUrl={eventWebappUrl} />}
                    </Flex>

                    {eventId && !creatingEvent && !isHidden('videoAI') && (
                        <FoldableSection title={t('videoAI.title')} defaultOpen={false}>
                            <EventVideoAI eventId={eventId} />
                        </FoldableSection>
                    )}

                    {eventId && !creatingEvent && (isSysAdmin || eventRole === 'eventadmin') && (
                        <FoldableSection title={t('templates.eventTitle')} defaultOpen={false}>
                            <EventEmailTemplates
                                eventId={eventId}
                                emailLanguage={eventData?.emailLanguage}
                            />
                        </FoldableSection>
                    )}

                    {(isSysAdmin || eventRole === 'eventadmin') && (
                        <FoldableSection title={t('eventUsers.title')} defaultOpen={sectionDefaultOpen}>
                            <EventUsers eventId={eventId} adminRole={typeAdminRole} typeLabel={typeConfig?.label?.[lang] || typeConfig?.label?.en} />
                        </FoldableSection>
                    )}
                </Stack>
            </Card.Body>

            {/* Delete confirmation dialog */}
            <Dialog.Root open={deleteDialogOpen} onOpenChange={(e) => setDeleteDialogOpen(e.open)} role="alertdialog">
                <Portal>
                    <Dialog.Backdrop />
                    <Dialog.Positioner>
                        <Dialog.Content>
                            <Dialog.Header>
                                <Dialog.Title>{t('events.deleteEventTitle')}</Dialog.Title>
                            </Dialog.Header>
                            <Dialog.Body>
                                <Text>{t('events.deleteEventConfirm', { name: eventData?.name || eventId })}</Text>
                            </Dialog.Body>
                            <Dialog.Footer>
                                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
                                <Button colorPalette="red" onClick={async () => { setDeleteDialogOpen(false); await handleDeleteEvent(eventId); }}>{t('common.delete')}</Button>
                            </Dialog.Footer>
                            <Dialog.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Dialog.CloseTrigger>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>

            <ChangeEventTypeDialog
                open={!!pendingType}
                onClose={() => setPendingType(null)}
                eventId={eventId}
                eventData={eventData}
                currentType={eventTypeId}
                nextType={pendingType || eventTypeId}
                onChanged={() => setPendingType(null)}
            />
        </Card.Root >

    )
}
